<template>
	<v-dialog
		:value="value"
		@input="$emit('input', $event)"
		max-width="900"
		scrollable
		persistent
		:fullscreen="fullScreen || $vuetify.breakpoint.xsOnly">
        <v-card class="background-2 round-card" :class="[$vuetify.breakpoint.smAndDown || fullScreen ? 'rounded-0' : 'round-card']">
            <v-card-title class="pa-3 background-1">
				<v-icon left>mdi-file-document-outline</v-icon>
				<p class="pl-2 pl-xl-3">{{ selectedTemplate ? 'Compila Template' : 'Seleziona Template WhatsApp' }}</p>
				<v-spacer></v-spacer>
				<v-tooltip top>
					<template v-slot:activator="{ on: tooltip }">					
						<v-btn v-on="{ ...tooltip }" icon @click.native.stop="fullScreen = !fullScreen">
							<v-icon>fullscreen</v-icon>
						</v-btn>
					</template>
					<span>Fullscreen</span>
				</v-tooltip>  
				<v-btn v-if="selectedTemplate" icon @click="back">
					<v-icon>mdi-arrow-left</v-icon>
				</v-btn>
			</v-card-title>

			<v-divider></v-divider>

			<!-- Template List -->
			<template v-if="!selectedTemplate">
				<v-card-text style="min-height: 400px" class="pa-0 background-2">
					<v-text-field
						v-model="search"
						prepend-icon="mdi-magnify"
						label="Cerca template..."
						single-line
						hide-details
						clearable
						dense
						outlined
						class="pa-4"
					></v-text-field>
					<v-divider></v-divider>
					<v-list two-line dense class="background-2 pt-0">
						<template v-for="(template, index) in filteredTemplates">
							<v-list-item
								:key="template.value"
								@click="selectTemplate(template)">
								<v-list-item-avatar>
									<v-icon>mdi-message-text</v-icon>
								</v-list-item-avatar>

								<v-list-item-content>
									<v-list-item-title>{{ template.text }}</v-list-item-title>
									<v-list-item-subtitle>{{ template.body | truncate(100) }}</v-list-item-subtitle>
								</v-list-item-content>

								<v-list-item-action>
									<v-chip
										color="success"
										small>
										Approvato
									</v-chip>
								</v-list-item-action>
							</v-list-item>
							<v-divider v-if="index < filteredTemplates.length - 1" :key="`divider-${template.value}`"></v-divider>
						</template>

						<v-list-item v-if="filteredTemplates.length === 0">
							<v-list-item-content>
								<v-list-item-title class="text-center">
									Nessun template trovato
								</v-list-item-title>
							</v-list-item-content>
						</v-list-item>
					</v-list>
				</v-card-text>
			</template>

				<!-- Template Variables Form -->
			<template v-else>
				<v-card-text style="min-height: 400px" class="pa-4 px-6 background-2">

					<!-- Template Name -->
					<v-subheader class="px-0 font-weight-bold">
						<v-icon small left color="primary">mdi-file-document</v-icon>
						{{ selectedTemplate.text }}
					</v-subheader>

					<v-subheader>
						<v-icon small left>mdi-variable</v-icon>
						Compila le variabili del template
					</v-subheader>

					<v-text-field
						v-for="(varInfo, varKey) in templateVariablesMap"
						:key="varKey"
						v-model="variablesValues[varKey]"
						:label="`Variabile {{${varKey}}}: ${varInfo}`"
						outlined
						dense
						:rules="[v => !!v || 'Campo obbligatorio']"
					></v-text-field>

					<!-- Template Preview -->
					<v-card outlined class="background-1 mt-4">
						<v-card-subtitle>
							<v-icon small left>mdi-eye</v-icon>
							ANTEPRIMA MESSAGGIO
						</v-card-subtitle>
						<v-divider></v-divider>
						<v-card-text>
							<div class="text-body-2" v-html="renderedTemplatePreview"></div>
							<!-- Media preview if template has media (after body text) -->
							<div v-if="templateMedia && templateMedia.length > 0" class="mt-3">
								<v-img
								v-for="(mediaUrl, index) in templateMedia"
								:key="index"
								:src="mediaUrl"
								max-width="300"
								class="rounded mb-2"
								></v-img>
							</div>
						</v-card-text>
					</v-card>
				</v-card-text>
			</template>

			<v-divider></v-divider>

			<v-card-actions class="background-1">
				<v-spacer></v-spacer>
				<v-btn
					text
					@click="cancel"
					>
					Annulla
				</v-btn>
				<v-btn
					v-if="selectedTemplate"
					color="primary"
					:disabled="!canSend"
					@click="sendTemplate"
					>
				<v-icon left>mdi-send</v-icon>
					Invia Template
				</v-btn>
			</v-card-actions>
		</v-card>
  	</v-dialog>
</template>

<script>

	import Vue from 'vue'

	export default {
		name: 'TemplateSelector',
		props: {
			value: {
				type: Boolean,
				default: false
			},
			conversationId: {
				type: Number,
				required: false,
				default: null
			},
		},

		data() {
			return {
				templates: [],
				search: '',
				loading: false,
				selectedTemplate: null,
				variablesValues: {},
				fullScreen: false,
			}
		},

		computed: {
			filteredTemplates() {
				if (!this.search) return this.templates

				const searchLower = this.search.toLowerCase()
				return this.templates.filter(template => {
					return template.name.toLowerCase().includes(searchLower) ||
						(template.body && template.body.toLowerCase().includes(searchLower))
				})
			},

			parsedOptions() {
				if (!this.selectedTemplate || !this.selectedTemplate.options) return null

				if (typeof this.selectedTemplate.options === 'string') {
					try {
						return JSON.parse(this.selectedTemplate.options)
					} catch (e) {
						return null
					}
				}
				return this.selectedTemplate.options
			},

			templateVariables() {
				if (!this.selectedTemplate || !this.selectedTemplate.body) return []

				// Match both numeric {{1}} and named {{first_name}} variables
				const matches = this.selectedTemplate.body.match(/\{\{([^}]+)\}\}/g)
				if (!matches) return []

				return [...new Set(matches.map(m => m.replace(/[{}]/g, '')))]
			},

			templateVariablesMap() {
				if (!this.selectedTemplate) return {}

				// Try to get variables info from options
				const options = this.parsedOptions
				if (options && options.twilio_content_json && options.twilio_content_json.variables) {
					return options.twilio_content_json.variables
				}

				// Fallback: create simple map
				const map = {}
				this.templateVariables.forEach(varKey => {
					map[varKey] = `Campo ${varKey}`
				})
				return map
			},

			templateMedia() {
				if (!this.selectedTemplate) return []

				const options = this.parsedOptions
				if (!options) return []

				// Check if template has media in options
				if (options.media && Array.isArray(options.media)) {
					return options.media
				}

				// Check in twilio_content_json
				if (options.twilio_content_json && options.twilio_content_json.types) {
					const types = options.twilio_content_json.types
					// Check twilio/media type
					if (types['twilio/media'] && types['twilio/media'].media) {
						return types['twilio/media'].media
					}
				}

				return []
			},

			renderedTemplatePreview() {
				if (!this.selectedTemplate) return ''

				let body = this.selectedTemplate.body

				Object.entries(this.variablesValues).forEach(([key, value]) => {
					const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g')
					body = body.replace(regex, value || `<span class="red--text">{{${key}}}</span>`)
				})

				return body.replace(/\n/g, '<br>')
			},

			canSend() {
				if (!this.selectedTemplate) return false

				// Check all variables are filled
				const missingVars = this.templateVariables.filter(varKey => !this.variablesValues[varKey])
				return missingVars.length === 0
			}
		},

	watch: {
		value(val) {
			if (val) {
				this.loadTemplates()
			} else {
				// Reset on close
				this.selectedTemplate = null
				this.variablesValues = {}
				this.search = ''
			}
		},
		selectedTemplate(newVal) {
			if (newVal) {
				// Reset variables values
				this.variablesValues = {}
			}
		}
	},

	filters: {
		truncate(value, length = 100) {
			if (!value) return ''
				value = value.toString()
			if (value.length <= length) return value
				return value.substring(0, length) + '...'
		}
	},

	methods: {
		async loadTemplates() {
			try {
				this.loading = true
				const response = await this.axios.get('get-whatsapp-templates')
				this.templates = response.data.data.templates || []
			} catch (error) {
				console.error('Error loading templates:', error)
				this.$store.commit('setSnackbar', {
					text: 'Errore nel caricamento dei template',
					color: 'error',
					value: true
				})
			} finally {
				this.loading = false
			}
		},

		selectTemplate(template) {
			this.selectedTemplate = template
		},

		back() {
			this.selectedTemplate = null
			this.variablesValues = {}
		},

		cancel() {
			this.selectedTemplate = null
			this.variablesValues = {}
			this.$emit('input', false)
		},

		sendTemplate() {
			if (!this.canSend) return

			this.$emit('template-selected', {
				template: this.selectedTemplate,
				variables: this.variablesValues
			})

			// Reset and close
			this.selectedTemplate = null
			this.variablesValues = {}
			this.$emit('input', false)
		},

		getStatusColor(status) {
			const colors = {
				'approved': 'success',
				'pending': 'warning',
				'rejected': 'error',
				'draft': 'grey'
			}
			return colors[status] || 'grey'
		}
	}
}
</script>

<style scoped>
	.v-list {
	overflow-y: auto;
	}
</style>
